import React, { useEffect, useState } from 'react';
import { XMarkIcon, ClipboardDocumentCheckIcon } from '@heroicons/react/24/outline';
import { FaCheck, FaXmark } from 'react-icons/fa6';
import axios from '../../../configApi/axiosConfig';
import { APIURL } from '../../../configApi/apiConfig';
import { CONTACTO_LABEL } from './ModalConversacion';

export const TIPOS_ERROR = [
    { valor: 'RUIDO', etiqueta: 'Ruido / audio malo' },
    { valor: 'VOCES', etiqueta: 'Voces superpuestas' },
    { valor: 'MONTOS', etiqueta: 'Montos mal' },
    { valor: 'FECHAS', etiqueta: 'Fechas mal' },
    { valor: 'NOMBRES', etiqueta: 'Nombres mal' },
    { valor: 'BUZON', etiqueta: 'Buzón tomado como contacto' },
    { valor: 'OTRO', etiqueta: 'Otro' },
];

const SENTIMIENTOS = ['POSITIVO', 'NEUTRO', 'NEGATIVO'];

const CAMPOS_OK = ['ContactoOk', 'ResultadoOk', 'PromesaOk', 'MontoOk', 'FechaPromesaOk', 'SentimientoOk'];

/** Número de campos en los que el revisor marcó que la IA se equivocó (null si no está revisada). */
export const diferenciasRevision = (g) => {
    if (!g?.Rev_Fecha) return null;
    return CAMPOS_OK.filter((c) => g[`Rev_${c}`] === false || g[`Rev_${c}`] === 0).length;
};

const soloFecha = (f) => (f ? String(f).split('T')[0] : '');

const formatFecha = (f) => {
    const [y, m, d] = soloFecha(f).split('-');
    return y && m && d ? `${parseInt(d, 10)}/${parseInt(m, 10)}/${y}` : '';
};

const aBool = (v) => (v === null || v === undefined ? null : Boolean(v));

const estadoInicial = (g) => ({
    ContactoOk: aBool(g.Rev_ContactoOk), ContactoReal: g.Rev_ContactoReal || '',
    ResultadoOk: aBool(g.Rev_ResultadoOk), ResultadoReal: g.Rev_ResultadoReal || '',
    PromesaOk: aBool(g.Rev_PromesaOk), PromesaReal: aBool(g.Rev_PromesaReal),
    MontoOk: aBool(g.Rev_MontoOk), MontoReal: g.Rev_MontoReal ?? '',
    FechaPromesaOk: aBool(g.Rev_FechaPromesaOk), FechaPromesaReal: soloFecha(g.Rev_FechaPromesaReal),
    SentimientoOk: aBool(g.Rev_SentimientoOk), SentimientoReal: g.Rev_SentimientoReal || '',
    CalidadTranscripcion: g.Rev_CalidadTranscripcion || null,
    TiposError: g.Rev_TiposError ? g.Rev_TiposError.split(',') : [],
    FragmentoCorregido: g.Rev_FragmentoCorregido || '',
    Observacion: g.Rev_Observacion || '',
});

const inputCls = 'w-full px-2 py-1 text-xs rounded-md bg-white border border-slate-300 text-slate-800 dark:bg-[#0D131F] dark:border-slate-700 dark:text-slate-200 focus:ring-1 focus:ring-indigo-500';

function BotonesOk({ valor, onChange }) {
    const base = 'p-1.5 rounded-md border transition-colors';
    return (
        <div className="flex gap-1">
            <button type="button" title="La IA acertó" onClick={() => onChange(valor === true ? null : true)}
                className={`${base} ${valor === true ? 'bg-emerald-600 border-emerald-600 text-white' : 'border-slate-300 text-slate-400 hover:text-emerald-600 dark:border-slate-700'}`}>
                <FaCheck className="text-[11px]" />
            </button>
            <button type="button" title="La IA se equivocó" onClick={() => onChange(valor === false ? null : false)}
                className={`${base} ${valor === false ? 'bg-rose-600 border-rose-600 text-white' : 'border-slate-300 text-slate-400 hover:text-rose-600 dark:border-slate-700'}`}>
                <FaXmark className="text-[11px]" />
            </button>
        </div>
    );
}

/**
 * Revisión humana de una gestión automática: el revisor escucha la llamada y marca, campo
 * por campo, si lo que detectó la IA es correcto o cuál es el valor real. Sirve para medir
 * la efectividad de la transcripción / análisis y mejorarlos con casos reales.
 */
export function ModalRevisionGestion({ gestion, idCompra, onClose, onGuardado }) {
    const [form, setForm] = useState(null);
    const [guardando, setGuardando] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        setForm(gestion ? estadoInicial(gestion) : null);
        setError('');
    }, [gestion]);

    if (!gestion || !form) return null;

    const set = (campo, valor) => setForm((f) => ({ ...f, [campo]: valor }));

    // Monto y fecha solo se evalúan cuando realmente hubo promesa (la de la IA o la corregida)
    const hayPromesa = form.PromesaOk === false ? form.PromesaReal === true : Boolean(gestion.PromesaPago);

    const filas = [
        {
            clave: 'Contacto', etiqueta: '¿Contestó? / Contacto',
            ia: CONTACTO_LABEL[gestion.Contacto] || (gestion.Contacto || '').replace(/_/g, ' '),
            real: (
                <select className={inputCls} value={form.ContactoReal} onChange={(e) => set('ContactoReal', e.target.value)}>
                    <option value="">Seleccione…</option>
                    {Object.entries(CONTACTO_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
            ),
        },
        {
            clave: 'Resultado', etiqueta: 'Resultado',
            ia: gestion.Resultado,
            real: <input className={inputCls} maxLength={100} value={form.ResultadoReal} placeholder="Resultado real"
                onChange={(e) => set('ResultadoReal', e.target.value)} />,
        },
        {
            clave: 'Promesa', etiqueta: 'Promesa de pago',
            ia: gestion.PromesaPago ? 'Sí' : 'No',
            real: (
                <select className={inputCls} value={form.PromesaReal === null ? '' : String(form.PromesaReal)}
                    onChange={(e) => set('PromesaReal', e.target.value === '' ? null : e.target.value === 'true')}>
                    <option value="">Seleccione…</option>
                    <option value="true">Sí</option>
                    <option value="false">No</option>
                </select>
            ),
        },
        ...(hayPromesa ? [
            {
                clave: 'Monto', etiqueta: 'Monto prometido',
                ia: gestion.MontoPrometido != null ? `$${Number(gestion.MontoPrometido).toFixed(2)}` : '',
                real: <input type="number" min="0" step="0.01" className={inputCls} value={form.MontoReal}
                    onChange={(e) => set('MontoReal', e.target.value)} />,
            },
            {
                clave: 'FechaPromesa', etiqueta: 'Fecha prometida',
                ia: formatFecha(gestion.FechaPrometida),
                real: <input type="date" className={inputCls} value={form.FechaPromesaReal}
                    onChange={(e) => set('FechaPromesaReal', e.target.value)} />,
            },
        ] : []),
        {
            clave: 'Sentimiento', etiqueta: 'Ánimo del cliente',
            ia: (gestion.SentimientoPersona || '').replace(/_/g, ' '),
            real: (
                <select className={inputCls} value={form.SentimientoReal} onChange={(e) => set('SentimientoReal', e.target.value)}>
                    <option value="">Seleccione…</option>
                    {SENTIMIENTOS.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
            ),
        },
    ];

    const validar = () => {
        const sinEvaluar = filas.filter((f) => form[`${f.clave}Ok`] === null).map((f) => f.etiqueta);
        if (sinEvaluar.length) return `Marca ✓ o ✗ en: ${sinEvaluar.join(', ')}`;
        if (form.ContactoOk === false && !form.ContactoReal) return 'Indica el contacto real.';
        if (form.ResultadoOk === false && !form.ResultadoReal.trim()) return 'Indica el resultado real.';
        if (form.PromesaOk === false && form.PromesaReal === null) return 'Indica si hubo promesa de pago.';
        if (form.SentimientoOk === false && !form.SentimientoReal) return 'Indica el ánimo real del cliente.';
        if (!form.CalidadTranscripcion) return 'Califica la transcripción (1 a 5).';
        return '';
    };

    const guardar = async () => {
        const msg = validar();
        if (msg) { setError(msg); return; }
        // Solo se envía el valor real de los campos marcados como incorrectos
        const real = (clave, valor) => (form[`${clave}Ok`] === false ? valor : undefined);
        const payload = {
            idCompra: Number(idCompra),
            ContactoOk: form.ContactoOk, ContactoReal: real('Contacto', form.ContactoReal),
            ResultadoOk: form.ResultadoOk, ResultadoReal: real('Resultado', form.ResultadoReal.trim()),
            PromesaOk: form.PromesaOk, PromesaReal: real('Promesa', form.PromesaReal),
            SentimientoOk: form.SentimientoOk, SentimientoReal: real('Sentimiento', form.SentimientoReal),
            CalidadTranscripcion: form.CalidadTranscripcion,
            TiposError: form.TiposError,
            FragmentoCorregido: form.FragmentoCorregido.trim() || undefined,
            Observacion: form.Observacion.trim() || undefined,
        };
        if (hayPromesa) {
            payload.MontoOk = form.MontoOk ?? undefined;
            payload.FechaPromesaOk = form.FechaPromesaOk ?? undefined;
            if (form.MontoOk === false && form.MontoReal !== '') payload.MontoReal = Number(form.MontoReal);
            if (form.FechaPromesaOk === false && form.FechaPromesaReal) payload.FechaPromesaReal = form.FechaPromesaReal;
        }
        setGuardando(true);
        setError('');
        try {
            await axios.patch(APIURL.revisionGestionAutomatica(gestion.idCbo_GestionesDeCobranzas), payload);
            onGuardado?.();
            onClose();
        } catch (e) {
            const m = e?.response?.data?.message;
            setError(Array.isArray(m) ? m.join(', ') : (m || 'No se pudo guardar la revisión.'));
        } finally {
            setGuardando(false);
        }
    };

    const alternarError = (valor) => set('TiposError', form.TiposError.includes(valor)
        ? form.TiposError.filter((t) => t !== valor)
        : [...form.TiposError, valor]);

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm" onClick={onClose}>
            <div className="bg-white dark:bg-[#111827] dark:border dark:border-slate-800 rounded-2xl shadow-2xl w-full max-w-5xl max-h-[92vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
                <div className="bg-gradient-to-r from-slate-800 to-slate-900 dark:from-[#0D131F] dark:to-[#0D131F] dark:border-b dark:border-slate-800 px-5 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="p-1.5 bg-indigo-500 rounded-lg">
                            <ClipboardDocumentCheckIcon className="w-4 h-4 text-white" />
                        </div>
                        <div>
                            <h3 className="text-sm font-bold text-white tracking-wide">OBSERVACIÓN DE LA GESTIÓN AUTOMÁTICA</h3>
                            <p className="text-blue-200 text-[11px]">
                                {gestion.Fecha ? new Date(gestion.Fecha).toLocaleString('es-EC') : ''} · {gestion.Telefono}
                                {gestion.Rev_Fecha && ` · Revisada por ${gestion.Rev_Usuario || '-'} el ${new Date(gestion.Rev_Fecha).toLocaleString('es-EC')}`}
                            </p>
                        </div>
                    </div>
                    <button type="button" onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/10" title="Cerrar">
                        <XMarkIcon className="w-5 h-5 text-white" />
                    </button>
                </div>

                <div className="overflow-y-auto p-5 grid md:grid-cols-2 gap-5">
                    {/* Fuente: audio + transcripción */}
                    <div className="space-y-3 min-w-0">
                        {gestion.UrlGrabacion
                            ? <audio controls preload="none" src={gestion.UrlGrabacion} className="w-full">Tu navegador no puede reproducir el audio.</audio>
                            : <p className="text-xs text-slate-400">Sin grabación.</p>}
                        <p className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Transcripción</p>
                        <div className="p-3 max-h-[55vh] overflow-y-auto bg-slate-50 rounded-xl border border-slate-200 text-xs leading-relaxed whitespace-pre-wrap text-slate-700 dark:bg-[#0D131F] dark:border-slate-800 dark:text-slate-300">
                            {(gestion.Transcript || 'Sin transcripción.').replace(/\s*(Operador|Cliente):\s*/g, '\n$1: ').trim()}
                        </div>
                    </div>

                    {/* Comparación IA vs real */}
                    <div className="space-y-4 min-w-0">
                        <table className="w-full text-xs">
                            <thead>
                                <tr className="text-[10px] uppercase tracking-wide text-slate-500 dark:text-slate-400">
                                    <th className="text-left pb-2">Campo</th>
                                    <th className="text-left pb-2">Detectó la IA</th>
                                    <th className="text-left pb-2">¿Correcto?</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                {filas.map((f) => (
                                    <React.Fragment key={f.clave}>
                                        <tr>
                                            <td className="py-2 pr-2 font-semibold text-slate-700 dark:text-slate-300">{f.etiqueta}</td>
                                            <td className="py-2 pr-2 text-slate-800 dark:text-slate-200">{f.ia || '-'}</td>
                                            <td className="py-2"><BotonesOk valor={form[`${f.clave}Ok`]} onChange={(v) => set(`${f.clave}Ok`, v)} /></td>
                                        </tr>
                                        {form[`${f.clave}Ok`] === false && (
                                            <tr className="border-t-0">
                                                <td className="pb-2 pr-2 text-[11px] text-rose-600 dark:text-rose-400 text-right">Real:</td>
                                                <td colSpan={2} className="pb-2">{f.real}</td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                ))}
                            </tbody>
                        </table>

                        <div>
                            <p className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-1">Calidad de la transcripción</p>
                            <div className="flex gap-1">
                                {[1, 2, 3, 4, 5].map((n) => (
                                    <button key={n} type="button" onClick={() => set('CalidadTranscripcion', n)}
                                        className={`w-8 h-8 rounded-md border text-xs font-bold ${form.CalidadTranscripcion === n
                                            ? 'bg-indigo-600 border-indigo-600 text-white'
                                            : 'border-slate-300 text-slate-600 hover:bg-slate-100 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800'}`}>
                                        {n}
                                    </button>
                                ))}
                                <span className="self-center text-[10px] text-slate-400 ml-2">1 = muy mala · 5 = perfecta</span>
                            </div>
                        </div>

                        <div>
                            <p className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-1">Errores detectados</p>
                            <div className="flex flex-wrap gap-1.5">
                                {TIPOS_ERROR.map((t) => (
                                    <button key={t.valor} type="button" onClick={() => alternarError(t.valor)}
                                        className={`px-2 py-0.5 rounded-full border text-[11px] ${form.TiposError.includes(t.valor)
                                            ? 'bg-amber-500 border-amber-500 text-white'
                                            : 'border-slate-300 text-slate-600 hover:bg-slate-100 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800'}`}>
                                        {t.etiqueta}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div>
                            <p className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-1">Fragmento corregido</p>
                            <textarea rows={2} maxLength={4000} className={inputCls} value={form.FragmentoCorregido}
                                placeholder='Transcribió: "ciento veinte" / Dijo: "veinte"'
                                onChange={(e) => set('FragmentoCorregido', e.target.value)} />
                        </div>

                        <div>
                            <p className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-1">Observación</p>
                            <textarea rows={2} maxLength={1000} className={inputCls} value={form.Observacion}
                                onChange={(e) => set('Observacion', e.target.value)} />
                        </div>
                    </div>
                </div>

                <div className="px-5 py-3 border-t border-slate-200 dark:border-slate-800 flex items-center justify-end gap-2">
                    {error && <p className="mr-auto text-xs text-rose-600 dark:text-rose-400">{error}</p>}
                    <button type="button" onClick={onClose}
                        className="px-3 py-1.5 text-xs rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-100 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800">
                        Cancelar
                    </button>
                    <button type="button" onClick={guardar} disabled={guardando}
                        className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white disabled:opacity-60">
                        {guardando ? 'Guardando…' : 'Guardar revisión'}
                    </button>
                </div>
            </div>
        </div>
    );
}
